// FILTERS: a schema's "only the entries where…" as conditions, as the backend's
// predicate, and in words. The realm editor writes them; the realm, contract and
// app panels say them the same way (components/nucleus/AccessList.js).
//
// The backend's predicate (authz/access.py
// _matches_predicate): a bare value is "is", an object names its operator, and
// every condition must hold. One written with $or (two realms folded together)
// cannot be shown as a list, so it is left to the YAML.
//
// DATES are compared as ISO text (_safe_cmp), so a day is a span: "on" is from the
// day's start to its end, "after" is past its end, "before" is short of its start.
// Days are UTC, as stored.
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

const DAY_END = 'T23:59:59.999Z';
const isDay = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
const isDayEnd = (v) => typeof v === 'string' && v.length === 10 + DAY_END.length && isDay(v.slice(0, 10)) && v.endsWith(DAY_END);
export const FILTER_OPS = [
    ['eq', 'is'], ['ne', 'is not'], ['in', 'is one of'], ['gt', 'is more than'], ['gte', 'is at least'], ['lt', 'is less than'], ['lte', 'is at most'],
    ['on', 'is on'], ['before', 'is before'], ['by', 'is on or before'], ['after', 'is after'], ['from', 'is on or after'],
    ['last', 'is in the last'], ['older', 'is older than'], ['next', 'is in the next'],
    ['contains', 'contains'], ['set', 'has a value'], ['unset', 'is empty'],
];
// conditions that stand without a value
export const VALUELESS = new Set(['set', 'unset']);
// a rolling span, counted from today each time it is read ($today±N d|w|mo,
// authz/access.py), its unit kept as written
export const SPAN_UNITS = [['days', 'd'], ['weeks', 'w'], ['months', 'mo']];
const spanToken = (value) => {
    const [n, unit] = String(value).trim().split(/\s+/);
    const code = SPAN_UNITS.find(([u]) => u === unit)?.[1] || 'd';
    const num = Math.floor(Number(n));
    return Number.isFinite(num) && num > 0 ? `${num}${code}` : null;
};
const spanOf = (n, code) => `${n} ${SPAN_UNITS.find(([, c]) => c === code)?.[0] || 'days'}`;
const ROLL = /^\$today(?:([+-])(\d+)(d|w|mo))?$/;
// what each kind of field can be compared by, read from its JSON schema
export const OPS_BY_KIND = {
    text: ['eq', 'ne', 'contains', 'in', 'set', 'unset'], choice: ['eq', 'ne', 'in', 'set', 'unset'],
    number: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'set', 'unset'], yesno: ['eq', 'set', 'unset'],
    date: ['last', 'older', 'next', 'on', 'before', 'by', 'after', 'from', 'set', 'unset'],
};
export function kindOfField(name, prop = {}) {
    const t = Array.isArray(prop.type) ? prop.type.find((x) => x !== 'null') : prop.type;
    if (Array.isArray(prop.enum) && prop.enum.length) return 'choice';
    if (t === 'boolean') return 'yesno';
    if (t === 'number' || t === 'integer') return 'number';
    if (t === 'array' || t === 'object') return 'list';
    if (prop.format === 'date' || prop.format === 'date-time' || /(^|_)(date|time|at|on|day)$|timestamp/i.test(name)) return 'date';
    return 'text';
}
export function conditionsOf(filter) {
    if (!filter) return [];
    if (Object.prototype.hasOwnProperty.call(filter, '$or')) return null;
    const out = [];
    for (const [field, cond] of Object.entries(filter)) {
        if (!isObj(cond)) { out.push({ field, op: 'eq', value: String(cond ?? '') }); continue; }
        const c = { ...cond };
        if (isDay(c.gte) && isDayEnd(c.lte) && c.lte.startsWith(c.gte)) { out.push({ field, op: 'on', value: c.gte }); delete c.gte; delete c.lte; }
        const ahead = typeof c.lt === 'string' && c.lt.match(ROLL);
        if (c.gte === '$today' && ahead && ahead[1] === '+') { out.push({ field, op: 'next', value: spanOf(Number(ahead[2]), ahead[3]) }); delete c.gte; delete c.lt; }
        for (const [op, v] of Object.entries(c)) {
            const roll = typeof v === 'string' && v.match(ROLL);
            if (op === 'exists') out.push({ field, op: v === true || v === 'true' ? 'set' : 'unset', value: '' });
            else if (op === 'gte' && roll && roll[1] === '-') out.push({ field, op: 'last', value: spanOf(Number(roll[2]), roll[3]) });
            else if (op === 'lt' && roll && roll[1] === '-') out.push({ field, op: 'older', value: spanOf(Number(roll[2]), roll[3]) });
            else if (op === 'lt' && isDay(v)) out.push({ field, op: 'before', value: v });
            else if (op === 'lte' && isDayEnd(v)) out.push({ field, op: 'by', value: v.slice(0, 10) });
            else if (op === 'gt' && isDayEnd(v)) out.push({ field, op: 'after', value: v.slice(0, 10) });
            else if (op === 'gte' && isDay(v)) out.push({ field, op: 'from', value: v });
            else out.push({ field, op, value: Array.isArray(v) ? v.join(', ') : String(v ?? '') });
        }
    }
    return out;
}
export function filterOf(conditions) {
    const out = {};
    const put = (field, op, v) => { out[field] = { ...(isObj(out[field]) ? out[field] : {}), [op]: v }; };
    for (const { field, op, value } of conditions) {
        const raw = String(value ?? '').trim();
        if (!field) continue;
        if (op === 'set' || op === 'unset') { put(field, 'exists', op === 'set'); continue; }
        if (raw === '') continue;
        if (op === 'last' || op === 'older' || op === 'next') {
            // last: since the same day that long ago; older: before it; next: from
            // today up to (not including) the day that far ahead
            const span = spanToken(raw);
            if (!span) continue;
            if (op === 'last') put(field, 'gte', `$today-${span}`);
            else if (op === 'older') put(field, 'lt', `$today-${span}`);
            else { put(field, 'gte', '$today'); put(field, 'lt', `$today+${span}`); }
            continue;
        }
        if (op === 'on') { put(field, 'gte', raw); put(field, 'lte', raw + DAY_END); } else if (op === 'before') put(field, 'lt', raw);
        else if (op === 'by') put(field, 'lte', raw + DAY_END);
        else if (op === 'after') put(field, 'gt', raw + DAY_END);
        else if (op === 'from') put(field, 'gte', raw);
        else if (op === 'in') put(field, 'in', raw.split(',').map((x) => x.trim()).filter(Boolean));
        else if (op === 'eq') out[field] = raw;
        else put(field, op, raw);
    }
    return Object.keys(out).length ? out : null;
}
export const filterWords = (filter) => {
    const c = conditionsOf(filter);
    if (c === null) return 'a filter set in YAML';
    return c.map((x) => [x.field, FILTER_OPS.find(([k]) => k === x.op)?.[1] || x.op, VALUELESS.has(x.op) ? '' : x.value].filter(Boolean).join(' ')).join(' and ');
};


/**
 * What of each schema a set of realms reads, in words, by schema id. Realms join
 * as a union: a schema any of them reaches whole is read whole, so a line shows
 * only when every realm reaching the schema narrows it ("Only title, mood";
 * "Only where type is run, or where type is ride").
 * `realms`: [{ masks: {sid: {fields?, filter?}}, schema_ids?: [sid], unrestricted? }].
 */
export function readNotes(realms = []) {
    const reaching = new Map();          // sid -> [mask | null]
    for (const r of realms) {
        const masks = r.masks || {};
        const ids = new Set([...(r.schema_ids || []).map(String), ...Object.keys(masks)]);
        for (const sid of ids) reaching.set(sid, [...(reaching.get(sid) || []), masks[sid] || null]);
    }
    const out = {};
    for (const [sid, list] of reaching) {
        const lines = [];
        if (list.every((m) => m && Array.isArray(m.fields))) lines.push(`Only ${[...new Set(list.flatMap((m) => m.fields))].join(', ')}`);
        if (list.every((m) => m && m.filter && Object.keys(m.filter).length)) lines.push(`Only where ${list.map((m) => filterWords(m.filter)).join(', or where ')}`);
        if (lines.length) out[sid] = lines;
    }
    return out;
}
